import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import { Empty, Loader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

import { WikiArticleCard } from "@/components/wiki/WikiArticleCard";
import { WikiChrome } from "@/components/wiki/WikiChrome";
import { WikiSearchBar } from "@/components/wiki/WikiSearchBar";

export default function WikiHome() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/wiki/home").then((res) => setData(res.data)).catch((error) => {
      toast.error(apiErr(error));
      setData({ hero: { title: "Lanista Wiki", subtitle: "The archives are not reachable right now." }, categories: [], featured_articles: [], popular_articles: [], recent_articles: [], quick_links: [], new_player_steps: [], live_sidebar: {}, is_admin: false });
    });
  }, []);

  if (!data) return <Loader label="Opening the archives…" />;

  const action = data.is_admin ? <Button asChild data-testid="wiki-admin-link" variant="outline" className="border-amber-700/40 bg-zinc-950/60 text-amber-100"><Link to="/admin/wiki">Wiki Admin</Link></Button> : null;

  return (
    <WikiChrome title={data.hero.title} subtitle={data.hero.subtitle} categories={data.categories} quickLinks={data.quick_links} liveSidebar={data.live_sidebar} action={action}>
      <section className="border border-amber-900/20 bg-zinc-950/55 p-5 sm:p-6 space-y-5">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Start here</div>
            <h2 className="mt-2 font-cinzel text-2xl text-amber-100">Learn the arena one system at a time.</h2>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed">Search the codex to learn the arena.</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-950/70 p-4">
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500">Search the archives</div>
            <WikiSearchBar className="mt-3" />
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-cinzel text-xl text-amber-200">Popular Topics</h2>
            <Link to="/wiki/search" className="text-xs uppercase tracking-[0.22em] text-zinc-500 hover:text-amber-300">Search all</Link>
          </div>
          {data.popular_articles.length === 0 ? <Empty label="No popular articles have been assigned yet." /> : <div className="grid gap-4 md:grid-cols-2">{data.popular_articles.map((article) => <WikiArticleCard key={article.id} article={article} />)}</div>}
        </div>

        <div className="space-y-4">
          <h2 className="font-cinzel text-xl text-amber-200">Recently Updated</h2>
          {data.recent_articles.length === 0 ? <Empty label="No wiki articles have been published yet." /> : <div className="space-y-3">{data.recent_articles.map((article) => <WikiArticleCard key={article.id} article={article} compact />)}</div>}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-cinzel text-xl text-amber-200">New Player Path</h2>
        {data.new_player_steps.length === 0 ? <Empty label="Create your onboarding path from Wiki Admin once articles are ready." /> : (
          <div className="grid gap-4 lg:grid-cols-3">
            {data.new_player_steps.map((step, index) => (
              <Link key={step.id || `${step.label}-${index}`} data-testid={`wiki-path-step-${index + 1}`} to={step.href || "/wiki"} className="border border-amber-900/20 bg-zinc-950/55 p-5 hover:border-amber-700/40 transition-colors">
                <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Step {index + 1}</div>
                <div className="mt-2 font-cinzel text-lg text-amber-100">{step.label}</div>
                {step.description && <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{step.description}</p>}
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="font-cinzel text-xl text-amber-200">Featured Articles</h2>
        {data.featured_articles.length === 0 ? <Empty label="Pick featured articles in Wiki Admin to fill this section." /> : <div className="grid gap-4 md:grid-cols-2">{data.featured_articles.map((article) => <WikiArticleCard key={article.id} article={article} />)}</div>}
      </section>
    </WikiChrome>
  );
}